import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { refreshCreativePerformance, isCreativeFeedQuery, type CreativeSavedData } from "@/lib/creative-performance-snapshot";
import { combineCreativeSnapshots, type CreativePerformanceSnapshotMetadata } from "@shared/creative-performance-snapshot";

export function CreativePerformanceRefresh({ brandId, snapshots, requestError }: {
  brandId: string; snapshots: Array<CreativePerformanceSnapshotMetadata | undefined>; requestError?: boolean;
}) {
  const client = useQueryClient();
  const [, setRevision] = useState(0);
  useEffect(() => client.getQueryCache().subscribe(event => {
    if (event.query && isCreativeFeedQuery(event.query.queryKey, brandId)) setRevision(value => value + 1);
  }), [client, brandId]);
  const [requesting, setRequesting] = useState(false);
  const active = client.getQueryCache().findAll({ predicate: query => query.isActive() && isCreativeFeedQuery(query.queryKey, brandId) });
  const dependencySnapshots = active.map(query => (query.state.data as CreativeSavedData<unknown> | undefined)?.snapshot);
  const metadata = combineCreativeSnapshots([...snapshots, ...dependencySnapshots].filter((item): item is CreativePerformanceSnapshotMetadata => !!item));
  const failed = requestError || active.some(query => !!query.state.error);
  return <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground" data-testid="creative-snapshot-status" role="status">
    <Button size="sm" variant="outline" disabled={requesting} onClick={async () => {
      setRequesting(true);
      try { await refreshCreativePerformance(client, brandId); } finally { setRequesting(false); }
    }} data-testid="button-cp-refresh">
      <RefreshCw className={`mr-2 h-3.5 w-3.5 ${requesting || metadata.state === "refreshing" ? "animate-spin" : ""}`} />
      {requesting || metadata.state === "refreshing" ? "Refreshing" : "Refresh"}
    </Button>
    {metadata.fetchedAt && <span>Fetched {new Date(metadata.fetchedAt).toLocaleString()}{metadata.stale ? " · Saved results" : ""}</span>}
    {(metadata.error || failed) && <span className="text-amber-700">Refresh failed. Saved results remain visible. {metadata.error}</span>}
  </div>;
}
